import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonButtonRowWidget } from '@eforall/common';
import { DialogService } from '@eforall/dashboard';
import { AppService, FuncService } from '../../../../../services';
import { VideoDialog, VideoDialogData } from '../../../dialog/video-dialog/video.dialog';
import { AccApplicationDialogDataService } from '../../acc-application-dialog-data.service';

@Component({
	selector: 'reset-participant-videos-part',
	standalone: true,
	imports: [
		CommonButtonRowWidget,
	],
	templateUrl: './reset-participant-videos.part.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './reset-participant-videos.part.scss'
})
export class ResetParticipantVideosPart {

	public readonly dialogDataService = inject(AccApplicationDialogDataService);
	private readonly func = inject(FuncService);
	private readonly dialogService = inject(DialogService);
	private app = inject(AppService);

	public readonly canManageApplication = computed(() => {
		const permissions = this.app.data().permissions;
		return permissions.isSoftwareDev || permissions.canManageAdmissionsStaff;
	});
	public readonly isResetting = signal<boolean>(false);
	public readonly isDeleting = signal<boolean>(false);

	public readonly allParticipants = computed(() => {
		const data = this.dialogDataService.dialogData();
		if (!data) return [];

		const participants = [];
		if (data.details?.participant) {
			participants.push(data.details.participant);
		}
		if (data.details?.coParticipants) {
			participants.push(...data.details.coParticipants);
		}
		return participants;
	});

	public async openVideoDialog(personId: number, uniqueVideoId: number) {
		if (!uniqueVideoId) return;

		const dialogData: VideoDialogData = {
			type: 'applicationParticipant',
			personId,
			uniqueVideoId
		}
		this.dialogService.showCustom(VideoDialog, { data: dialogData }, 500, 550);
	}

	public async resetVideo(accApplicationParticipantId: number, personId: number, uniqueVideoId: number) {

		const yes = await this.dialogService.confirm('Are you sure you want to reset this participant\'s video? This will remove the video reference from the database. The video file will remain in storage.', 'Yes', 'No', [], 400, 250);

		if (!yes) return;

		this.isResetting.set(true);
		try {

			await this.func.accApplicationParticipant.resetVideo({
				accApplicationParticipantId,
				personId,
				uniqueVideoId,
				isDelete: false
			});
			await this.dialogDataService.reloadData();
		} catch (error) {
			console.error('Error resetting video:', error);
			await this.dialogService.showMessage('Error', 'Failed to reset video. Please try again.');
		} finally {
			this.isResetting.set(false);
		}
	}

	public async deleteVideo(accApplicationParticipantId: number, personId: number, uniqueVideoId: number) {

		const yes = await this.dialogService.confirm('Are you sure you want to delete this participant\'s video? This action cannot be undone. The video file will be permanently removed from storage.', 'Yes', 'No', [], 400, 250);

		if (!yes) return;

		this.isDeleting.set(true);
		try {
			await this.func.accApplicationParticipant.resetVideo({
				accApplicationParticipantId,
				personId,
				uniqueVideoId,
				isDelete: true
			});
			await this.dialogDataService.reloadData();
		} catch (error) {
			console.error('Error deleting video:', error);
			await this.dialogService.showMessage('Error', 'Failed to delete video. Please try again.');
		} finally {
			this.isDeleting.set(false);
		}
	}
}
